import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Modal,
  message,
  Divider,
  Table,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  LinkOutlined,
  DisconnectOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getSocialAccount,
  deleteSocialAccount,
  connectSocialAccount,
  disconnectSocialAccount,
  refreshSocialAccountToken,
  getSocialPosts,
  getSocialMessages,
  type SocialAccount,
  type SocialPost,
  type SocialMessage,
} from '@/api/socialApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  expired: 'red',
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'blue',
  instagram: 'magenta',
  twitter: 'cyan',
  linkedin: 'geekblue',
  wechat: 'green',
};

const POST_STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  scheduled: 'orange',
  published: 'green',
  failed: 'red',
};

export default function SocialAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [account, setAccount] = useState<SocialAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [messages, setMessages] = useState<SocialMessage[]>([]);

  const canWrite = hasPermission('social_accounts', 'write');
  const canDelete = hasPermission('social_accounts', 'delete');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getSocialAccount(id)
      .then((data) => {
        setAccount(data);
        getSocialPosts({ account_id: id, from: 0, size: 10 })
          .then((res) => setPosts(res.items))
          .catch(() => setPosts([]));
        getSocialMessages({ account_id: id, from: 0, size: 10 })
          .then((res) => setMessages(res.items))
          .catch(() => setMessages([]));
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('social.action.delete_confirm_account'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteSocialAccount(id);
          message.success(t('social.delete_account_success'));
          navigate('/social');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  const handleLifecycle = useCallback(
    async (action: 'connect' | 'disconnect' | 'refresh_token') => {
      if (!id) return;
      try {
        if (action === 'connect') await connectSocialAccount(id);
        else if (action === 'disconnect') await disconnectSocialAccount(id);
        else await refreshSocialAccountToken(id);
        message.success(t(`social.action.${action}_success`));
        const updated = await getSocialAccount(id);
        setAccount(updated);
      } catch {
        message.error(t('app.error'));
      }
    },
    [id, t]
  );

  const postColumns: ColumnsType<SocialPost> = [
    {
      title: t('social.field.content'),
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string, record: SocialPost) => (
        <a onClick={() => navigate(`/social/posts/${record.id}`)}>
          {content.substring(0, 80)}{content.length > 80 ? '...' : ''}
        </a>
      ),
    },
    {
      title: t('social.field.post_type'),
      dataIndex: 'post_type',
      key: 'post_type',
      width: 80,
      render: (type: string) => t(`social.post_type.${type}`, type),
    },
    {
      title: t('social.field.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={POST_STATUS_COLORS[status] ?? 'default'}>
          {t(`social.status.${status}`, status)}
        </Tag>
      ),
    },
    {
      title: t('social.field.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
    },
  ];

  const messageColumns: ColumnsType<SocialMessage> = [
    {
      title: t('social.field.content'),
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string, record: SocialMessage) => (
        <a onClick={() => navigate(`/social/messages/${record.id}`)}>
          {content.substring(0, 80)}{content.length > 80 ? '...' : ''}
        </a>
      ),
    },
    {
      title: t('social.field.direction'),
      dataIndex: 'direction',
      key: 'direction',
      width: 100,
      render: (dir: string) => (
        <Tag color={dir === 'inbound' ? 'blue' : 'green'}>
          {t(`social.direction.${dir}`, dir)}
        </Tag>
      ),
    },
    {
      title: t('social.field.status'),
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'unread' ? 'orange' : 'default'}>
          {t(`social.status.${status}`, status)}
        </Tag>
      ),
    },
    {
      title: t('social.field.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
    },
  ];

  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!account) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/social')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button icon={<EditOutlined />} onClick={() => navigate(`/social/accounts/${id}/edit`)}>
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
        {account.status !== 'active' && canWrite && (
          <Button icon={<LinkOutlined />} onClick={() => handleLifecycle('connect')}>
            {t('social.action.connect')}
          </Button>
        )}
        {account.status === 'active' && canWrite && (
          <Button icon={<DisconnectOutlined />} onClick={() => handleLifecycle('disconnect')}>
            {t('social.action.disconnect')}
          </Button>
        )}
        {canWrite && (
          <Button icon={<ReloadOutlined />} onClick={() => handleLifecycle('refresh_token')}>
            {t('social.action.refresh_token')}
          </Button>
        )}
      </Space>

      <h2>{t('social.account_detail_title')}</h2>

      <Descriptions bordered column={2}>
        <Descriptions.Item label={t('social.field.account_name')} span={2}>
          {account.account_name}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.platform')}>
          <Tag color={PLATFORM_COLORS[account.platform] ?? 'default'}>
            {t(`social.platform.${account.platform}`, account.platform)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.status')}>
          <Tag color={STATUS_COLORS[account.status] ?? 'default'}>
            {t(`social.status.${account.status}`, account.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.account_id')}>
          {account.account_id}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.follower_count')}>
          {account.follower_count?.toLocaleString() ?? '0'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.avatar_url')}>
          {account.avatar_url || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.profile_url')} span={2}>
          {account.profile_url ? (
            <a href={account.profile_url} target="_blank" rel="noopener noreferrer">
              {account.profile_url}
            </a>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.token_expires_at')}>
          {account.token_expires_at ? new Date(account.token_expires_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.branch_id')}>
          {account.branch_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.created_by')}>
          {account.created_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.created_at')}>
          {new Date(account.created_at).toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.updated_by')}>
          {account.updated_by}
        </Descriptions.Item>
        <Descriptions.Item label={t('social.field.updated_at')}>
          {new Date(account.updated_at).toLocaleString()}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <h3>{t('social.posts_title')}</h3>
      <Table<SocialPost>
        columns={postColumns}
        dataSource={posts}
        rowKey="id"
        size="small"
        pagination={false}
      />

      <Divider />
      <h3>{t('social.messages_title')}</h3>
      <Table<SocialMessage>
        columns={messageColumns}
        dataSource={messages}
        rowKey="id"
        size="small"
        pagination={false}
      />
    </div>
  );
}
